import { LocationQuery } from 'vue-router';

interface RouteLike {
    path: string;
    query: LocationQuery;
    hash: string;
}

interface LangRedirect {
    path: string;
    query: LocationQuery;
    hash: string;
    replace: true;
}

/**
 * A repeated parameter ("?lang=es&lang=fr") arrives as an array. Picking one
 * would be a guess, so neither is used.
 */
function readLang(query: LocationQuery): string | null {
    const lang = query.lang;
    return typeof lang === 'string' && lang ? lang : null;
}

/**
 * Reduce a query to just the language, for redirects that deliberately discard
 * everything else.
 *
 * The `select-interview` redirect drops its query so the rprId does not trail
 * through the rest of the flow. That route is the landing point for a
 * verification link, so without this the language would be discarded by the very
 * first navigation, before any guard could preserve it.
 */
export function pickLangQuery(query: LocationQuery): LocationQuery {
    const lang = readLang(query);
    return lang ? { lang } : {};
}

/**
 * Work out whether a navigation needs the language added back.
 *
 * Keeping the parameter in the address bar is what makes a refresh keep its
 * language, since the URL is the only thing a reload has to go on.
 *
 * @returns where to navigate instead, or null to let the navigation proceed.
 */
export function preserveLangQuery(to: RouteLike, from: RouteLike): LangRedirect | null {
    const lang = readLang(from.query);
    if (!lang || readLang(to.query)) {
        return null;
    }
    return {
        path: to.path,
        query: { ...to.query, lang },
        hash: to.hash,
        // Replace so the language-less URL does not become a back-button trap.
        replace: true,
    };
}
